import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Table, {
  ColumnDefinitionType,
} from '../../../../../components/Table';
import MyModal from '../../../../../components/Modal';
import FormInput from '../../../../../components/Form/FormInput';
import FormSelectBox from '../../../../../components/Form/FormSelectBox';
import {
  Peligro,
  Fase,
  RiesgoEvaluacion,
} from '../../../../../types/AnalisisRiesgo';
import { ConceptoRiesgoDTO } from '../../../../../types/ConceptoRiesgo';
import { useAnalisisRiesgo } from '../../../../../hooks/useAnalisisRiesgo';
import { useRiesgos } from '../../../../../hooks/useRiesgos';
import { downloadFileParam } from '../../../../../services/Service';
import {
  APROBADA_STATUS_ID,
  EN_ACTUALIZACION_STATUS_ID,
} from '../../../../../constants/constants';
import { useSolicitud } from '../../../../../hooks/useSolicitud';
import { toast } from 'react-toastify';
import api from '../../../../../api/api';
import './styles.css';
import { logger } from '../../../../../utils/logger';

interface EvaluacionRiesgosPersonalesProps {
  isDisabled?: boolean;
  isEditing?: boolean;
  // Props del hook useAnalisisRiesgo
  fasesPersonales?: Fase[];
  agregarFase?: (
    descripcion: string,
    tipo?: 'PERSONAL' | 'ENTORNO',
  ) => void;
  editarFase?: (
    id: number,
    nuevaDescripcion: string,
    tipo?: 'PERSONAL' | 'ENTORNO',
  ) => void;
  agregarPeligro?: (
    faseId: number,
    peligro: Omit<Peligro, 'id'>,
    tipo?: 'PERSONAL' | 'ENTORNO',
  ) => void;
  editarPeligro?: (
    faseId: number,
    peligroId: number,
    peligroActualizado: Partial<Omit<Peligro, 'id'>>,
    tipo?: 'PERSONAL' | 'ENTORNO',
  ) => void;
  eliminarPeligroById?: (
    faseId: number,
    id: string,
  ) => Promise<boolean>;
  handlePeligroSelected?: (peligro: Peligro) => void;
  peligroSelected?: Peligro;
  actualizarRiesgoEvaluacion?: (
    faseId: number,
    campo: keyof RiesgoEvaluacion,
    valor: number,
    tipo?: 'PERSONAL' | 'ENTORNO',
  ) => void;
  eliminarFase?: (id: number, tipo?: 'PERSONAL' | 'ENTORNO') => void;
}

const EvaluacionRiesgosPersonales: React.FC<
  EvaluacionRiesgosPersonalesProps
> = ({
  isDisabled = false,
  isEditing = true,
  // Props del hook
  fasesPersonales: propsFasesPersonales,
  agregarFase: propsAgregarFase,
  editarFase: propsEditarFase,
  agregarPeligro: propsAgregarPeligro,
  editarPeligro: propsEditarPeligro,
  eliminarPeligroById: propsEliminarPeligroById,
  handlePeligroSelected: propsHandlePeligroSelected,
  peligroSelected: propsPeligroSelected,
  actualizarRiesgoEvaluacion: propsActualizarRiesgoEvaluacion,
  eliminarFase: propsEliminarFase,
}) => {
  const [nuevaFase, setNuevaFase] = useState('');
  const [isConfirmDeletePeligro, setIsConfirmDeletePeligro] =
    useState(false);
  const [isConfirmDeleteFase, setIsConfirmDeleteFase] =
    useState(false);
  const [faseIdToDelete, setFaseIdToDelete] = useState<number>();
  const [peligroIdSelect, setPeligroIdSelect] = useState<number>();
  const [currentFaseId, setCurrentFaseId] = useState<number>();
  const [isVisualizar, setIsVisualizar] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Estados para el formulario de agregar peligro
  const [peligroFormData, setPeligroFormData] = useState({
    nombre: '',
    barreraAdministrativa: '',
    barreraFisica: '',
    eppEspecifico: '',
  });

  // Estado para manejar errores de validación
  const [formErrors, setFormErrors] = useState({
    nombre: '',
    barreraAdministrativa: '',
    barreraFisica: '',
    eppEspecifico: '',
  });

  // Estado local para los conceptos de exposición
  const [conceptosExposicion, setConceptosExposicion] = useState<
    ConceptoRiesgoDTO[]
  >([]);

  // Estado local para los conceptos de consecuencia
  const [conceptosConsecuencia, setConceptosConsecuencia] = useState<
    ConceptoRiesgoDTO[]
  >([]);

  // Estado local para los conceptos de probabilidad
  const [conceptosProbabilidad, setConceptosProbabilidad] = useState<
    ConceptoRiesgoDTO[]
  >([]);

  // Lista de peligros predefinidos - ahora se obtiene del backend
  const {
    peligrosPersonales,
    obtenerPeligrosPersonales,
    obtenerConceptosExposicion,
    obtenerConceptosConsecuencia,
    obtenerConceptosProbabilidad,
  } = useRiesgos();

  const { solicitudSelected } = useSolicitud();

  // Función para descargar el reporte de identificación de peligros
  const descargarReporte = async (solicitudId: number) => {
    try {
      const response = await api.get(
        `reportes/identificacion-peligros/${solicitudId}`,
        { responseType: 'blob' },
      );

      const blob = new Blob([response.data], {
        type: 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `identificacion_peligros_${solicitudId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url); // Liberar memoria
    } catch (error) {
      logger.error(
        'Error al descargar el reporte:',
        logger.safeError(error),
      );
      toast.error('Error al descargar el reporte');
    }
  };

  // Convertir los peligros del backend al formato esperado por el componente
  const opcionesPeligros = peligrosPersonales.map((peligro) => ({
    id: peligro.nombre,
    descripcion: peligro.nombre,
  }));

  // Convertir los conceptos de exposición del backend al formato esperado por el componente
  // Si no hay datos del backend, usar valores por defecto
  const opcionesExposicion =
    conceptosExposicion.length > 0
      ? conceptosExposicion.map((concepto) => ({
          id: Number(concepto.valor), // Asegurar que sea number (BigDecimal del backend)
          descripcion: concepto.valor.toString(),
        }))
      : [
          // Valores por defecto en caso de que no se puedan cargar desde el backend
          { id: 0.1, descripcion: '0.1' },
          { id: 0.3, descripcion: '0.3' },
          { id: 0.6, descripcion: '0.6' },
          { id: 1, descripcion: '1' },
          { id: 3, descripcion: '3' },
          { id: 6, descripcion: '6' },
          { id: 10, descripcion: '10' },
        ];

  const {
    fasesPersonales: hookFasesPersonales,
    peligroSelected: hookPeligroSelected,
    actualizarRiesgoEvaluacion: hookActualizarRiesgoEvaluacion,
    agregarFase: hookAgregarFase,
    editarFase: hookEditarFase,
    agregarPeligro: hookAgregarPeligro,
    editarPeligro: hookEditarPeligro,
    handlePeligroSelected: hookHandlePeligroSelected,
    eliminarPeligroById: hookEliminarPeligroById,
    eliminarFase: hookEliminarFase,
  } = useAnalisisRiesgo();

  // Usar props si están disponibles, sino usar el hook local
  const fasesPersonales = propsFasesPersonales || hookFasesPersonales;
  const peligroSelected = propsPeligroSelected || hookPeligroSelected;
  const actualizarRiesgoEvaluacion =
    propsActualizarRiesgoEvaluacion || hookActualizarRiesgoEvaluacion;
  const agregarFase = propsAgregarFase || hookAgregarFase;
  const editarFase = propsEditarFase || hookEditarFase;
  const agregarPeligroHook =
    propsAgregarPeligro || hookAgregarPeligro;
  const editarPeligroHook = propsEditarPeligro || hookEditarPeligro;
  const handlePeligroSelected =
    propsHandlePeligroSelected || hookHandlePeligroSelected;
  const eliminarPeligroById =
    propsEliminarPeligroById || hookEliminarPeligroById;
  const eliminarFase = propsEliminarFase || hookEliminarFase;

  // Usar las fases personales específicamente
  const fases = fasesPersonales;

  // Log para monitorear cambios en fasesPersonales
  useEffect(() => {
    logger.log('=== FASES PERSONALES ACTUALIZADAS ===');
    logger.log(
      'Nuevas fasesPersonales, cantidad:',
      fasesPersonales?.length,
    );
    logger.log('Cantidad de fases:', fasesPersonales?.length || 0);
    fasesPersonales?.forEach((fase, index) => {
      logger.log(`Fase ${index + 1} (id: ${fase.id}):`, {
        descripcion: fase.descripcion,
        cantidadPeligros: fase.peligros?.length || 0,
        peligros: fase.peligros,
      });
    });
  }, [fasesPersonales]);

  // Cargar los peligros y conceptos de exposición y consecuencia cuando el componente se monta
  useEffect(() => {
    obtenerPeligrosPersonales();

    // Cargar conceptos de exposición y setearlos en estado local
    const cargarConceptosExposicion = async () => {
      const conceptos = await obtenerConceptosExposicion();
      setConceptosExposicion(conceptos);
    };

    // Cargar conceptos de consecuencia y setearlos en estado local
    const cargarConceptosConsecuencia = async () => {
      const conceptos = await obtenerConceptosConsecuencia();
      setConceptosConsecuencia(conceptos);
    };

    // Cargar conceptos de probabilidad y setearlos en estado local
    const cargarConceptosProbabilidad = async () => {
      const conceptos = await obtenerConceptosProbabilidad();
      setConceptosProbabilidad(conceptos);
    };

    cargarConceptosExposicion();
    cargarConceptosConsecuencia();
    cargarConceptosProbabilidad();
  }, [
    obtenerPeligrosPersonales,
    obtenerConceptosExposicion,
    obtenerConceptosConsecuencia,
    obtenerConceptosProbabilidad,
  ]);

  // Función para crear columnas de la tabla de peligros para una fase específica
  const createColumnsForFase = (
    faseId: number,
  ): ColumnDefinitionType<Peligro, keyof Peligro>[] => {
    const columns: ColumnDefinitionType<Peligro, keyof Peligro>[] =
      [];

    columns.push({
      key: 'nombre',
      header: 'PELIGRO',
    });

    columns.push({
      key: 'barreraFisica',
      header: 'BARRERA FÍSICA',
    });

    columns.push({
      key: 'barreraAdministrativa',
      header: 'BARRERA ADMINIST.',
    });

    columns.push({
      key: 'eppEspecifico',
      header: 'EPP ESPECÍFICO',
    });

    columns.push({
      key: 'usuarioUltimaEdicion',
      header: 'USUARIO',
    });

    columns.push({
      key: 'id',
      header: 'ACCIONES',
      cell: (data: Peligro) => (
        <>
          <Link
            title="Visualizar"
            to="/exportar"
            onClick={(e) => {
              visualizarPeligro(data);
              e.preventDefault();
            }}
            target="_blank"
          >
            <i
              className="fa fa-eye text-secondary me-2"
              aria-hidden="true"
            ></i>
          </Link>
          {isEditing && (
            <Link
              title="Editar"
              to=""
              onClick={(e) => {
                editarPeligro(data, faseId);
                e.preventDefault();
              }}
              target="_blank"
            >
              <i
                className="fa fa-pencil text-secondary me-2"
                aria-hidden="true"
              ></i>
            </Link>
          )}
          {isEditing && (
            <Link
              title="Eliminar"
              to=""
              onClick={(e) => {
                setPeligroIdSelect(data.id);
                setCurrentFaseId(faseId);
                setIsConfirmDeletePeligro(true);
                e.preventDefault();
              }}
              target="_blank"
            >
              <i
                className="fa fa-trash text-secondary me-2"
                aria-hidden="true"
              ></i>
            </Link>
          )}
        </>
      ),
    });

    return columns;
  };

  const handleAgregarFase = () => {
    // if (nuevaFase.trim()) {
    agregarFase(nuevaFase, 'PERSONAL');
    setNuevaFase('');
    // }
  };

  const agregarPeligro = (faseId: number) => {
    setCurrentFaseId(faseId);
    // Establecer peligroSelected con id 0 para modo agregar
    handlePeligroSelected({
      id: 0,
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    });
    setPeligroFormData({
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    });
    setIsVisualizar(false);
    setModalOpen(true);
  };

  const editarPeligro = (peligro: Peligro, faseId: number) => {
    handlePeligroSelected(peligro);
    setCurrentFaseId(faseId);
    setPeligroFormData({
      nombre: peligro.nombre,
      barreraAdministrativa: peligro.barreraAdministrativa,
      barreraFisica: peligro.barreraFisica,
      eppEspecifico: peligro.eppEspecifico,
    });
    setIsVisualizar(false);
    setModalOpen(true);
  };

  const visualizarPeligro = (peligro: Peligro) => {
    handlePeligroSelected(peligro);
    setIsVisualizar(true);
    setModalOpen(true);
  };

  const handleFormChange = (field: string, value: string) => {
    setPeligroFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error cuando el usuario empiece a escribir
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Función para validar el formulario
  const validateForm = (): boolean => {
    const errors = {
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    };

    let isValid = true;

    // Validar que el peligro esté seleccionado
    if (!peligroFormData.nombre.trim()) {
      errors.nombre = 'Debe seleccionar un peligro';
      isValid = false;
    }

    // Validar barrera administrativa
    if (!peligroFormData.barreraAdministrativa.trim()) {
      errors.barreraAdministrativa =
        'La barrera administrativa es obligatoria';
      isValid = false;
    }

    // Validar barrera física
    if (!peligroFormData.barreraFisica.trim()) {
      errors.barreraFisica = 'La barrera física es obligatoria';
      isValid = false;
    }

    // Validar EPP específico
    if (!peligroFormData.eppEspecifico.trim()) {
      errors.eppEspecifico = 'El EPP específico es obligatorio';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSavePeligro = () => {
    // Validar el formulario antes de guardar
    if (!validateForm()) {
      return;
    }

    if (currentFaseId && peligroFormData.nombre.trim()) {
      // Evitar peligros duplicados dentro de la misma fase.
      // El id de un peligro corresponde al del catálogo, por lo que repetir
      // el mismo peligro genera filas con id idéntico y, al eliminar/editar
      // una, se afectarían todas las coincidencias.
      const faseActual = fases.find((f) => f.id === currentFaseId);
      const yaExiste = faseActual?.peligros.some(
        (p) =>
          p.nombre.trim().toLowerCase() ===
            peligroFormData.nombre.trim().toLowerCase() &&
          p.id !== peligroSelected.id,
      );

      if (yaExiste) {
        toast.error('El peligro ya fue agregado en esta fase');
        return;
      }

      if (peligroSelected.id === 0) {
        // Agregar nuevo peligro
        agregarPeligroHook(
          currentFaseId,
          {
            nombre: peligroFormData.nombre,
            barreraAdministrativa:
              peligroFormData.barreraAdministrativa,
            barreraFisica: peligroFormData.barreraFisica,
            eppEspecifico: peligroFormData.eppEspecifico,
          },
          'PERSONAL',
        );
      } else {
        // Editar peligro existente
        editarPeligroHook(
          currentFaseId,
          peligroSelected.id,
          {
            nombre: peligroFormData.nombre,
            barreraAdministrativa:
              peligroFormData.barreraAdministrativa,
            barreraFisica: peligroFormData.barreraFisica,
            eppEspecifico: peligroFormData.eppEspecifico,
          },
          'PERSONAL',
        );
      }
      handleCloseModal();
    }
  };

  const handleConfirmDeleteFase = () => {
    if (faseIdToDelete !== undefined) {
      eliminarFase(faseIdToDelete, 'PERSONAL');
    }
    setIsConfirmDeleteFase(false);
    setFaseIdToDelete(undefined);
  };

  const handleConfirmDeletePeligro = async () => {
    if (peligroIdSelect && currentFaseId) {
      try {
        await eliminarPeligroById(
          currentFaseId,
          peligroIdSelect.toString(),
        );
      } catch (error) {
        logger.error(
          'Error al eliminar peligro:',
          logger.safeError(error),
        );
      } finally {
        setIsConfirmDeletePeligro(false);
      }
    } else {
      logger.error(
        'No se puede eliminar: falta peligroIdSelect o currentFaseId',
      );
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setIsVisualizar(false);
    setPeligroFormData({
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    });
    setFormErrors({
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    });
  };

  // Opciones para los selectores de evaluación de riesgo
  // Convertir los conceptos de probabilidad del backend al formato esperado por el componente
  // Si no hay datos del backend, usar valores por defecto
  const opcionesProbabilidad =
    conceptosProbabilidad.length > 0
      ? conceptosProbabilidad.map((concepto) => ({
          id: Number(concepto.valor), // Asegurar que sea number (BigDecimal del backend)
          descripcion: concepto.valor.toString(),
        }))
      : [
          // Valores por defecto en caso de que no se puedan cargar desde el backend
          { id: 0.1, descripcion: '0.1' },
          { id: 0.3, descripcion: '0.3' },
          { id: 0.6, descripcion: '0.6' },
          { id: 1, descripcion: '1' },
          { id: 3, descripcion: '3' },
          { id: 6, descripcion: '6' },
          { id: 10, descripcion: '10' },
        ];

  // Convertir los conceptos de consecuencia del backend al formato esperado por el componente
  // Si no hay datos del backend, usar valores por defecto
  const opcionesConsecuencia =
    conceptosConsecuencia.length > 0
      ? conceptosConsecuencia.map((concepto) => ({
          id: Number(concepto.valor), // Asegurar que sea number (BigDecimal del backend)
          descripcion: concepto.valor.toString(),
        }))
      : [
          // Valores por defecto en caso de que no se puedan cargar desde el backend
          { id: 1, descripcion: '1' },
          { id: 3, descripcion: '3' },
          { id: 7, descripcion: '7' },
          { id: 15, descripcion: '15' },
          { id: 40, descripcion: '40' },
          { id: 100, descripcion: '100' },
        ];

  return (
    <>
      {/* Modal de confirmación para eliminar peligro */}
      <MyModal
        isOpen={isConfirmDeletePeligro}
        toggle={() => setIsConfirmDeletePeligro(false)}
        title="Advertencia"
        cancelButton={true}
        secondButton={true}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={handleConfirmDeletePeligro}
      >
        <label>¿Confirma la eliminación del Peligro?</label>
      </MyModal>

      {/* Modal de confirmación para eliminar fase */}
      <MyModal
        isOpen={isConfirmDeleteFase}
        toggle={() => setIsConfirmDeleteFase(false)}
        title="Advertencia"
        cancelButton={true}
        secondButton={true}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={handleConfirmDeleteFase}
      >
        <label>
          Se perderá toda la información de la fase. ¿Confirma la
          eliminación?
        </label>
      </MyModal>

      {/* Modal para ver/editar peligro */}
      <MyModal
        size="lg"
        isOpen={modalOpen}
        toggle={handleCloseModal}
        cancelButton={true}
        secondButton={!isVisualizar}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={handleSavePeligro}
        title={
          isVisualizar
            ? 'Vista Peligro'
            : peligroSelected.id === 0
              ? 'Agregar Peligro'
              : 'Editar Peligro'
        }
      >
        <div className="container-fluid modal-peligro-form">
          {isVisualizar ? (
            // Vista para visualizar peligro - mismo formulario pero deshabilitado
            <div>
              <div className="row mb-4">
                <FormSelectBox
                  title={'Peligro'}
                  label={'peligro'}
                  id={'peligro-select-view'}
                  name={'peligro-select-view'}
                  optionId={'descripcion'}
                  optionLabel={'descripcion'}
                  value={peligroSelected.nombre}
                  options={opcionesPeligros}
                  isDisabled={true}
                  classLabel={'col-form-label col-3 text-end'}
                  classDivSelect={'col-9 select-peligro'}
                  onChange={() => {}}
                />
              </div>

              <div className="barrera-mitigacion">
                <h6 className="mb-3">Barrera de Mitigación</h6>
                <div className="row mb-3">
                  <FormInput
                    id={'barrera-fisica-view'}
                    label={'Barrera Física'}
                    name={'barrera-fisica-view'}
                    value={peligroSelected.barreraFisica}
                    onChange={() => {}}
                    placeholder={''}
                    isDisabled={true}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                </div>
                <div className="row mb-3">
                  <FormInput
                    id={'barrera-administrativa-view'}
                    label={'Barrera Administrativa'}
                    name={'barrera-administrativa-view'}
                    value={peligroSelected.barreraAdministrativa}
                    onChange={() => {}}
                    placeholder={''}
                    isDisabled={true}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                </div>

                <div className="row mb-3">
                  <FormInput
                    id={'epp-especifico-view'}
                    label={'EPP Específico'}
                    name={'epp-especifico-view'}
                    value={peligroSelected.eppEspecifico}
                    onChange={() => {}}
                    placeholder={''}
                    isDisabled={true}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                </div>
              </div>
            </div>
          ) : (
            // Formulario para agregar/editar peligro
            <div>
              <div className="row mb-4">
                <FormSelectBox
                  title={'Peligro *'}
                  label={'peligro'}
                  id={'peligro-select'}
                  name={'peligro-select'}
                  optionId={'descripcion'}
                  optionLabel={'descripcion'}
                  value={peligroFormData.nombre}
                  options={opcionesPeligros}
                  isDisabled={false}
                  classLabel={'col-form-label col-3 text-end'}
                  classDivSelect={'col-9 select-peligro'}
                  onChange={(e) =>
                    handleFormChange('nombre', e.target.value)
                  }
                />
                {formErrors.nombre && (
                  <div className="col-9 offset-3">
                    <small className="text-danger">
                      {formErrors.nombre}
                    </small>
                  </div>
                )}
              </div>

              <div className="barrera-mitigacion">
                <h6 className="mb-3">Barrera de Mitigación</h6>
                <div className="row mb-3">
                  <FormInput
                    id={'barrera-fisica'}
                    label={'Barrera Física *'}
                    name={'barrera-fisica'}
                    value={peligroFormData.barreraFisica}
                    onChange={(e) =>
                      handleFormChange(
                        'barreraFisica',
                        e.target.value,
                      )
                    }
                    placeholder={'Ingrese la barrera física'}
                    maxLength={500}
                    isDisabled={false}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                  {formErrors.barreraFisica && (
                    <div className="col-8 offset-4">
                      <small className="text-danger">
                        {formErrors.barreraFisica}
                      </small>
                    </div>
                  )}
                </div>

                <div className="row mb-3">
                  <FormInput
                    id={'barrera-administrativa'}
                    label={'Barrera Administrativa *'}
                    name={'barrera-administrativa'}
                    value={peligroFormData.barreraAdministrativa}
                    onChange={(e) =>
                      handleFormChange(
                        'barreraAdministrativa',
                        e.target.value,
                      )
                    }
                    placeholder={'Ingrese la barrera administrativa'}
                    maxLength={500}
                    isDisabled={false}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                  {formErrors.barreraAdministrativa && (
                    <div className="col-8 offset-4">
                      <small className="text-danger">
                        {formErrors.barreraAdministrativa}
                      </small>
                    </div>
                  )}
                </div>

                <div className="row mb-3">
                  <FormInput
                    id={'epp-especifico'}
                    label={'EPP Específico *'}
                    name={'epp-especifico'}
                    value={peligroFormData.eppEspecifico}
                    onChange={(e) =>
                      handleFormChange(
                        'eppEspecifico',
                        e.target.value,
                      )
                    }
                    placeholder={'Ingrese el EPP específico'}
                    maxLength={500}
                    isDisabled={false}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                  {formErrors.eppEspecifico && (
                    <div className="col-8 offset-4">
                      <small className="text-danger">
                        {formErrors.eppEspecifico}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </MyModal>

      <div className="evaluacion-riesgos-container">
        <div className="mb-4">
          {/* Link para descargar reporte - Solo visible cuando está APROBADA */}
          {(solicitudSelected.idTipoEstado === APROBADA_STATUS_ID ||
            solicitudSelected.idTipoEstado ===
              EN_ACTUALIZACION_STATUS_ID) && (
            <div className="mb-3">
              <Link
                to="/exportar"
                onClick={(e) => {
                  e.preventDefault();
                  descargarReporte(solicitudSelected.id);
                }}
                target="_blank"
                className="text-primary"
                style={{ fontSize: '0.95rem', fontWeight: '500' }}
              >
                <i
                  className="fa fa-file-pdf-o me-1"
                  aria-hidden="true"
                ></i>
                Descargar Reporte de Identificación de Peligros
              </Link>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-0">
                Evaluación de Riesgos Personales
              </h5>
              <Link
                to="/exportar"
                onClick={(e) => {
                  e.preventDefault();
                  downloadFileParam(
                    'tiposDocumentos/descargarArchivo',
                    'Anexo+I+Evaluación+del+riesgo.pdf',
                    'Anexo+I+Evaluación+del+riesgo.pdf',
                  );
                }}
                target="_blank"
                className="text-primary"
                style={{ fontSize: '0.9rem' }}
              >
                <i
                  className="fa fa-download me-1"
                  aria-hidden="true"
                ></i>
                Guía para la Evaluación de Riesgos
              </Link>
            </div>
            {isEditing && (
              <div className="d-flex gap-2">
                {/* <FormInput
                  id={'nuevaFase'}
                  label={''}
                  name={'nuevaFase'}
                  placeholder="Descripción de la nueva fase"
                  value={nuevaFase}
                  onChange={(e) => setNuevaFase(e.target.value)}
                  isDisabled={false}
                  classColumn={'nueva-fase-input'}
                /> */}
                <Button
                  className="btn-primary"
                  variant="primary"
                  onClick={handleAgregarFase}
                  //disabled={!nuevaFase.trim()}
                  disabled={isDisabled}
                >
                  Agregar Fase
                </Button>
              </div>
            )}
          </div>

          {/* Sección de Fases */}
          {fases.map((fase) => (
            <div key={fase.id} className="mb-4 fase-container p-3">
              <div className="row mb-3 align-items-end">
                <div className="col-md-10">
                  <div className="d-flex align-items-end gap-2">
                    <div className="fase-numero-container">
                      <label className="form-label">FASE</label>
                      <FormInput
                        id={`fase-${fase.id}`}
                        label={''}
                        name={`fase-${fase.id}`}
                        value={fase.nroFase.toString()}
                        isDisabled={true}
                        classColumn={'fase-numero-input'}
                      />
                    </div>
                    <div
                      className="flex-grow-1"
                      title={
                        fase.descripcion.length > 100
                          ? fase.descripcion
                          : ''
                      }
                      style={{
                        cursor:
                          fase.descripcion.length > 100 &&
                          (isDisabled || !isEditing)
                            ? 'help'
                            : 'auto',
                      }}
                    >
                      <FormInput
                        id={`descripcion-${fase.id}`}
                        label={''}
                        name={`descripcion-${fase.id}`}
                        placeholder="Descripción de la fase"
                        value={fase.descripcion}
                        onChange={(e) =>
                          editarFase(
                            fase.id,
                            e.target.value,
                            'PERSONAL',
                          )
                        }
                        maxLength={500}
                        isDisabled={isDisabled || !isEditing}
                        classColumn={''}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-2 text-end">
                  {isEditing && (
                    <div className="d-flex flex-column gap-2 align-items-end">
                      <Button
                        className="btn-primary"
                        variant="primary"
                        onClick={() => agregarPeligro(fase.id)}
                        size="sm"
                        disabled={isDisabled}
                      >
                        Agregar Peligro
                      </Button>
                      {!isDisabled && (
                        <Button
                          variant="danger"
                          onClick={() => {
                            setFaseIdToDelete(fase.id);
                            setIsConfirmDeleteFase(true);
                          }}
                          size="sm"
                        >
                          Eliminar Fase
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Tabla de Peligros */}
              <div className="table-responsive">
                <Table
                  data={fase.peligros}
                  columns={createColumnsForFase(fase.id)}
                ></Table>
              </div>

              <div className="riesgo-calculation">
                {/* Primera fila: Exposición, Probabilidad, Consecuencia */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="row">
                      <FormSelectBox
                        title={'Exposición'}
                        label={`exposicion-${fase.id}`}
                        id={`exposicion-${fase.id}`}
                        name={`exposicion-${fase.id}`}
                        optionId={'id'}
                        optionLabel={'descripcion'}
                        value={fase.evaluacionRiesgo.exposicion || ''}
                        options={opcionesExposicion}
                        // isDisabled={isDisabled || !isEditing}
                        isDisabled={!isEditing}
                        classLabel={'col-form-label col-5 text-end'}
                        classDivSelect={'col-7'}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          actualizarRiesgoEvaluacion(
                            fase.id,
                            'exposicion',
                            newValue,
                            'PERSONAL',
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="row">
                      <FormSelectBox
                        title={'Probabilidad'}
                        label={`probabilidad-${fase.id}`}
                        id={`probabilidad-${fase.id}`}
                        name={`probabilidad-${fase.id}`}
                        optionId={'id'}
                        optionLabel={'descripcion'}
                        value={
                          fase.evaluacionRiesgo.probabilidad || ''
                        }
                        options={opcionesProbabilidad}
                        // isDisabled={isDisabled || !isEditing}
                        isDisabled={!isEditing}
                        classLabel={'col-form-label col-5 text-end'}
                        classDivSelect={'col-7'}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          actualizarRiesgoEvaluacion(
                            fase.id,
                            'probabilidad',
                            newValue,
                            'PERSONAL',
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="row">
                      <FormSelectBox
                        title={'Consecuencia'}
                        label={`consecuencia-${fase.id}`}
                        id={`consecuencia-${fase.id}`}
                        name={`consecuencia-${fase.id}`}
                        optionId={'id'}
                        optionLabel={'descripcion'}
                        value={
                          fase.evaluacionRiesgo.consecuencia || ''
                        }
                        options={opcionesConsecuencia}
                        // isDisabled={isDisabled || !isEditing}
                        isDisabled={!isEditing}
                        classLabel={'col-form-label col-5 text-end'}
                        classDivSelect={'col-7'}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          actualizarRiesgoEvaluacion(
                            fase.id,
                            'consecuencia',
                            newValue,
                            'PERSONAL',
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Segunda fila: Riesgo y Nivel de Riesgo */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="row">
                      <FormInput
                        id={`riesgo-${fase.id}`}
                        label={'Riesgo'}
                        name={`riesgo-${fase.id}`}
                        value={fase.evaluacionRiesgo.riesgo.toFixed(
                          2,
                        )}
                        isDisabled={true}
                        classLabel={'col-form-label col-4 text-end'}
                        classColumn={'col-8'}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="row">
                      <FormInput
                        id={`nivelRiesgo-${fase.id}`}
                        label={'Nivel de Riesgo'}
                        name={`nivelRiesgo-${fase.id}`}
                        value={fase.evaluacionRiesgo.nivelRiesgo}
                        isDisabled={true}
                        classLabel={'col-form-label col-4 text-end'}
                        classColumn={'col-8'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default EvaluacionRiesgosPersonales;
